import Link from 'components/link'
import { MultiStepWizard } from 'components/presentation'
import { parseErrors } from 'lib/api-utils'
import { useState, useEffect } from 'react'
import ShippingAddressForm from 'registry/forms/shipping-address-form'
import { SYSTEM_ERROR_KEY } from 'shared/constants'
import AddressSuggestionForm from '../settings-page/shipping-address/address-suggestion-form'
import ConfirmAddressForm from '../settings-page/shipping-address/confirm-address-form'
import { ConsolidatedShippingSubtext } from 'registry/forms/consolidated-shipping-form-fields'
import { connect } from 'react-redux'
import {
  saveConsolidationChoice,
  saveShippingAddress,
  setActiveModal,
} from 'registry/actions'
import {
  getShippingConsolidation,
  getRegistryId,
  getRegistryOwnerId,
} from 'registry/reducers'
import { HELLO_BABY_BOX } from 'registry/constants'
import { useTracking, track } from 'lib/analytics'
import { FlagsProvider, useFeature } from 'flagged'

interface AddShippingAddressWizardProps {
  onCancel: (shouldOpenHBBModal: boolean) => void
  dispatch: (args: any) => void
  onSubmitSuccess: (shouldOpenHBBModal: boolean) => void
  currentConsolidationChoice: string | null
  registryId: number
  registryOwnerId: number
  isOpeningFromHBBGoalsModal?: boolean
}

const AddShippingAddressWizard = (props: AddShippingAddressWizardProps) => {
  const {
    onCancel,
    dispatch,
    onSubmitSuccess,
    currentConsolidationChoice,
    registryId,
    registryOwnerId,
    isOpeningFromHBBGoalsModal = false,
  } = props
  const hbbFreeShippingIsEnabled = useFeature(
    'hbb_free_shipping_with_simple_shipping_enabled'
  )
  const [activeStep, setActiveStep] = useState<number>(1)
  const [address, setAddress] = useState(null)
  const [enteredAddress, setEnteredAddress] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [recommendedAddress, setRecommendedAddress] = useState(null)
  const tracker = useTracking()
  const eventProps: {
    registryId: number
    registryOwnerId: number
    eventLocation?: string
  } = { registryId, registryOwnerId }
  if (isOpeningFromHBBGoalsModal) {
    eventProps.eventLocation =
      track.EventLocation.HELLO_BABY_BOX_MINUS_SHIPPING_CONSOLIDATION
  }

  // track if showing the consolidation choice
  useEffect(() => {
    if (activeStep == 1) {
      tracker.trackEvent({
        event: track.shippingConsolidationViewed,
        ...eventProps,
      })
    }
  }, [activeStep])

  const onBack = () => {
    setActiveStep(1)
    setAddress(enteredAddress)
    setRecommendedAddress(null)
  }

  const onSave = (values: any) => {
    values.userConfirmed = false
    if (values.consolidationChoice != currentConsolidationChoice) {
      tracker.trackEvent({
        event: track.shippingConsolidationUpdated,
        consolidationValue: values.consolidationChoice,
        ...eventProps,
      })
      dispatch(saveConsolidationChoice(values.consolidationChoice))
    }
    return dispatch(saveShippingAddress(values))
  }

  const onSubmitFail = (resp: any) => {
    const { address, recommendedAddress, fieldErrors } = resp
    if (address) {
      setEnteredAddress({
        streetAddress1: address.address1,
        streetAddress2: address.address2,
        ...address,
      })
      setFieldErrors(fieldErrors)
      setRecommendedAddress(recommendedAddress)
      setActiveStep(2)
    } else {
      const error = parseErrors(resp)
      const message = error[SYSTEM_ERROR_KEY]
      const variant = 'danger'

      PubSub.publish(BLConstants.FLASH_MESSAGE, { variant, message })
    }
  }

  return (
    <FlagsProvider
      features={{
        showHbbFreeShippingCopy:
          isOpeningFromHBBGoalsModal && hbbFreeShippingIsEnabled,
      }}
    >
      <MultiStepWizard activeStep={activeStep}>
        {/* step 1 */}
        <ShippingAddressForm
          showCancelButton
          address={address}
          onCancel={() => onCancel(isOpeningFromHBBGoalsModal)}
          onSave={onSave}
          onSubmitFail={onSubmitFail}
          onSubmitSuccess={() => onSubmitSuccess(isOpeningFromHBBGoalsModal)}
          showConsolidationChoice={true}
          renderSubText={ConsolidatedShippingSubtext}
        />
        {/* step 2 */}
        <div>
          <div className="mbl">
            <Link onClick={onBack}>
              <i className="fa fa-angle-left" /> Back
            </Link>
          </div>
          {recommendedAddress ? (
            <AddressSuggestionForm
              enteredAddress={enteredAddress}
              fieldErrors={fieldErrors}
              recommendedAddress={recommendedAddress}
              onEdit={onBack}
            />
          ) : (
            <ConfirmAddressForm
              enteredAddress={enteredAddress}
              fieldErrors={fieldErrors}
              onEdit={onBack}
            />
          )}
        </div>
      </MultiStepWizard>
    </FlagsProvider>
  )
}

const mapDispatchtoProps = (dispatch: any) => ({
  dispatch,
  onCancel: (shouldOpenHBBModal: boolean) => {
    const modalToOpen = shouldOpenHBBModal ? HELLO_BABY_BOX : null
    dispatch(setActiveModal(modalToOpen))
  },
  onSubmitSuccess: (shouldOpenHBBModal: boolean) => {
    const modalToOpen = shouldOpenHBBModal ? HELLO_BABY_BOX : null
    dispatch(setActiveModal(modalToOpen))

    PubSub.publish(BLConstants.FLASH_MESSAGE, {
      variant: 'success',
      message: 'Your shipping address has been updated!',
    })
  },
})

const mapStateToProps = (state: any) => {
  return {
    currentConsolidationChoice:
      getShippingConsolidation(state)?.consolidationChoice,
    registryId: getRegistryId(state),
    registryOwnerId: getRegistryOwnerId(state),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchtoProps
)(AddShippingAddressWizard)
